'use client';

import { useEffect, useState } from 'react';
import { getBasePath } from '../utils/basePath';
import { fetchJsonOnce, fetchJsonWithBackoff } from './fetchJson';
import type { TelegramItem } from './MediaItemTypes';
import { sortByNewest } from './sortByNewest';

export const fallbackTelegramItems: TelegramItem[] = [
  {
    id: 'fallback-1',
    url: 'https://t.me/s/joevnoise',
    date: null,
    source: 'telegram',
    type: 'lyric',
    title: 'Words from the writing desk',
    excerpt: 'Collected poems and writings from the joev noise Telegram channel.',
    text: 'Words from the writing desk, collected from telegram channel posts.',
    checked: false,
    show: true,
    curated: false,
  },
];

/**
 * Normalizes a raw input object into a typed TelegramItem.
 */
function normalizeTelegramItem(input: Partial<TelegramItem>): TelegramItem | null {
  if (!input.url) return null;

  const id = input.id || input.url.split('/').pop();
  if (!id) return null;
  if (input.show === false) return null;

  return {
    id,
    url: input.url,
    date: input.date || null,
    source: 'telegram',
    type: input.type || 'text',
    title: input.title || 'Untitled telegram post',
    excerpt: input.excerpt || input.text || '',
    text: input.text || input.excerpt || '',
    checked: input.checked ?? false,
    show: input.show ?? true,
    curated: input.curated ?? false,
  };
}

/**
 * React hook for fetching and managing Telegram lyric and poem entries.
 */
export function useTelegramItem() {
  const [items, setItems] = useState<TelegramItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryToken, setRetryToken] = useState(0);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const fetcher = retryToken > 0 ? fetchJsonWithBackoff : fetchJsonOnce;
      const basePath = getBasePath();
      const result = await fetcher<{ items?: Partial<TelegramItem>[] }>(
        `${basePath}/data/telegram-lyrics.json`
      );

      if (!result.ok) {
        setItems(sortByNewest(fallbackTelegramItems));
        setError(result.reason === 'exception' ? result.error : null);
        setLoading(false);
        return;
      }

      const fetchedItems = Array.isArray(result.data.items) ? result.data.items : [];
      const normalizedItems = fetchedItems
        .map((item) => normalizeTelegramItem(item))
        .filter((item): item is TelegramItem => Boolean(item));

      setItems(sortByNewest(normalizedItems.length ? normalizedItems : fallbackTelegramItems));
      setError(null);
      setLoading(false);
    };

    load();
  }, [retryToken]);

  const retry = () => setRetryToken((token) => token + 1);

  return {
    items,
    loading,
    error,
    retry,
  };
}
